import React, { useState, useCallback, useEffect, useRef } from 'react';
import { STORAGE_KEY, IMPREJURARI_DATA, getInitialFormData, loadFromStorage } from '../constants';
import { AccidentSketch, ManualSketchGrid, RadioGroup } from '../components';
import { SCENARIO_LIST } from '../sketchScenarios';

// Exemple pentru descriere accident
const EXEMPLE_DESCRIERI = [
  "La intersectie am virat la stanga. Celalalt sofer venea din sens opus si m-a lovit in dreapta.",
  "Eram oprit la semafor. Vehiculul din spate nu a franat la timp si m-a lovit in spate.",
  "Ieseam din parcare cu spatele. Alt vehicul trecea pe culoar si ne-am lovit lateral.",
  "Circulam intr-un sens giratoriu. Celalalt vehicul a intrat fara sa acorde prioritate."
];

// Definitia pasilor wizard - 5 PASI (vehicule unite)
const WIZARD_STEPS = [
  { 
    id: 1, 
    title: 'Date accident', 
    icon: '📍',
    greeting: '👋 Hai sa incepem!',
    description: 'Spune-ne cand si unde s-a intamplat accidentul. Dureaza sub un minut.'
  },
  { 
    id: 2, 
    title: 'Vehicule', 
    icon: '🚗',
    greeting: '🚗 Datele vehiculelor',
    description: 'Completeaza datele ambelor masini implicate in accident.'
  },
  { 
    id: 3, 
    title: 'Imprejurari', 
    icon: '📋',
    greeting: '📋 Ce s-a intamplat?',
    description: 'Bifeaza situatiile care descriu accidentul pentru fiecare vehicul. E important sa fiti de acord amandoi!'
  },
  { 
    id: 4, 
    title: 'Schita', 
    icon: '🎨',
    greeting: '🎨 Deseneaza accidentul',
    description: 'Poti genera automat o schita cu AI sau alege un scenariu predefinit. Sau lasa grila pentru desen manual.'
  },
  { 
    id: 5, 
    title: 'Finalizare', 
    icon: '✅',
    greeting: '🎉 Aproape gata!',
    description: 'Adauga observatii daca e cazul, verifica datele si descarca PDF-ul. Nu uita sa semnati amandoi!'
  }
];

function FormularPage() {
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-top">
            <div className="hero-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Gratuit si 100% sigur
            </div>
            <h1 className="hero-title">
              Completeaza <span className="highlight">Constatarea Amiabila</span> in 5 minute
            </h1>
            <p className="hero-subtitle">
              Ai avut un accident usor? Completeaza formularul online, 
              genereaza PDF-ul Amiabila si rezolva dauna rapid, fara stres.
            </p>
            <button className="hero-cta" onClick={scrollToForm}>
              Completeaza Amiabila
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M19 12l-7 7-7-7"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-it-works">
        <div className="how-it-works-container">
          <div className="section-header-new">
            <p className="section-label">Cum functioneaza</p>
            <h2 className="section-title-new">3 pasi simpli catre constatarea amiabila</h2>
            <p className="section-subtitle-new">Direct pe telefon sau PC</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">📝</div>
              <h3 className="step-title">Completezi datele</h3>
              <p className="step-desc">
                Introdu datele celor 2 vehicule, asigurarile si circumstantele accidentului.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">🤖</div>
              <h3 className="step-title">Generam schita</h3>
              <p className="step-desc">
                Descrie accidentul in cuvinte si AI-ul creeaza automat schita sau o desenezi manual.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">📄</div>
              <h3 className="step-title">Descarci PDF-ul</h3>
              <p className="step-desc">
                Primesti formularul Amiabila completat, gata de semnat de ambii conducatori.
              </p>
            </div>
          </div>

          <div className="pdf-preview-section">
            <img 
              src="/constatare-amiabila-preview.jpg" 
              alt="Preview Constatare Amiabila completata" 
              className="pdf-preview-image"
            />
            <div className="pdf-preview-content">
              <h3>Formularul Amiabila, completat automat</h3>
              <p>
                Descarci documentul direct completat. Ai gresit? Nicio problema! Modifici si generezi din nou.
                Acceptat de toate companiile de asigurari din Romania.
              </p>
              <div className="pdf-features">
                <div className="pdf-feature">
                  <span className="pdf-feature-icon">✔</span>
                  Format standard european
                </div>
                <div className="pdf-feature">
                  <span className="pdf-feature-icon">✔</span>
                  Toate campurile pre-completate
                </div>
                <div className="pdf-feature">
                  <span className="pdf-feature-icon">✔</span>
                  Schita accidentului inclusa
                </div>
                <div className="pdf-feature">
                  <span className="pdf-feature-icon">✔</span>
                  Gata de semnat si trimis la asigurator
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <div ref={formRef}>
        <FormContent />
      </div>
    </>
  );
}

// ========== STEP INTRO COMPONENT ==========
function StepIntro({ step }) {
  return (
    <div className="step-intro">
      <div className="step-intro-badge">Pasul {step.id} din 5</div>
      <h2 className="step-intro-greeting">{step.greeting}</h2>
      <p className="step-intro-description">{step.description}</p>
    </div>
  );
}

// ========== WIZARD STEPPER COMPONENT ==========
function WizardStepper({ currentStep, onStepClick, completedSteps }) {
  return (
    <div className="wizard-stepper">
      {WIZARD_STEPS.map((step, index) => {
        const isActive = step.id === currentStep;
        const isCompleted = completedSteps.includes(step.id);
        const isClickable = step.id <= Math.max(...completedSteps, currentStep);
        
        return (
          <React.Fragment key={step.id}>
            <div 
              className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={() => isClickable && onStepClick(step.id)}
            >
              <div className="wizard-step-icon">
                {isCompleted && !isActive ? '✓' : step.icon}
              </div>
              <div className="wizard-step-title">{step.title}</div>
            </div>
            {index < WIZARD_STEPS.length - 1 && (
              <div className={`wizard-connector ${isCompleted ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ========== FORM CONTENT ==========
function FormContent() {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', error: false });
  const [sketchMode, setSketchMode] = useState('manual');
  const [descriereAccident, setDescriereAccident] = useState('');
  const [sketchData, setSketchData] = useState(null);
  const [sketchLoading, setSketchLoading] = useState(false);
  const [sketchError, setSketchError] = useState('');
  const [formData, setFormData] = useState(loadFromStorage);
  const [wantEmail, setWantEmail] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  
  const formContainerRef = useRef(null);
  
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.error('Eroare la salvarea datelor:', e);
    }
  }, [formData]);

  const showToast = useCallback((message, error = false) => {
    setToast({ show: true, message, error });
    setTimeout(() => setToast({ show: false, message: '', error: false }), 3000);
  }, []);

  const updateFormData = useCallback((path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  const toggleImprejurare = useCallback((vehicle, id) => {
    const key = vehicle === 'A' ? 'imprejurariA' : 'imprejurariB';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(id) ? prev[key].filter(i => i !== id) : [...prev[key], id]
    }));
  }, []);

  const handleSketchModeChange = useCallback((mode) => {
    setSketchMode(mode);
    if (mode === 'manual') {
      setSketchData(null);
      setFormData(prev => ({ ...prev, sketchData: null }));
      setDescriereAccident('');
      setSketchError('');
    }
  }, []);

  const copyOwnerToDriver = useCallback((vehicle) => {
    const prefix = `vehicul${vehicle}`;
    setFormData(prev => {
      const vehicleData = prev[prefix];
      const asigurat = vehicleData.asigurat;
      return {
        ...prev,
        [prefix]: {
          ...vehicleData,
          conducatorEsteProprietar: true,
          conducator: {
            ...vehicleData.conducator,
            nume: asigurat.nume,
            prenume: asigurat.prenume,
            adresa: asigurat.adresa,
            tara: asigurat.tara,
            tel: asigurat.tel
          }
        }
      };
    });
    showToast(`Date copiate pentru Vehicul ${vehicle}`);
  }, [showToast]);

  const uncheckOwnerToDriver = useCallback((vehicle) => {
    const prefix = `vehicul${vehicle}`;
    setFormData(prev => ({
      ...prev,
      [prefix]: { ...prev[prefix], conducatorEsteProprietar: false }
    }));
  }, []);

  const resetForm = useCallback(() => {
    if (window.confirm('Sigur doriti sa stergeti toate datele din formular?')) {
      setFormData(getInitialFormData());
      setSketchData(null);
      setDescriereAccident('');
      setSketchError('');
      setSketchMode('manual');
      setCurrentStep(1);
      setCompletedSteps([]);
      localStorage.removeItem(STORAGE_KEY);
      showToast('Formularul a fost resetat');
    }
  }, [showToast]);

  const scrollToFormTop = useCallback(() => {
    setTimeout(() => {
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
    scrollToFormTop();
  }, [scrollToFormTop]);

  const nextStep = useCallback(() => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }
    if (currentStep < WIZARD_STEPS.length) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, completedSteps, goToStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const genereazaSchita = useCallback(async () => {
    if (!descriereAccident.trim()) {
      setSketchError('Va rugam descrieti accidentul.');
      return;
    }
    setSketchLoading(true);
    setSketchError('');
    try {
      const response = await fetch('/api/generate-sketch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descriere: descriereAccident })
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Serverul nu a returnat JSON. Reporneste serverul.');
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.details || data.error || 'Eroare');
      setSketchData(data.sketchData);
      setFormData(prev => ({ ...prev, sketchData: data.sketchData }));
      showToast('Schita a fost generata!');
    } catch (e) {
      setSketchError(e.message);
    } finally {
      setSketchLoading(false);
    }
  }, [descriereAccident, showToast]);

  const selectScenario = useCallback((scenarioId) => {
    if (!scenarioId) {
      setSketchData(null);
      setFormData(prev => ({ ...prev, sketchData: null }));
      return;
    }
    const newSketchData = { scenarioId };
    setSketchData(newSketchData);
    setFormData(prev => ({ ...prev, sketchData: newSketchData }));
    showToast('Scenariu selectat');
  }, [showToast]);

  const generatePDF = useCallback(async () => {
    const required = [
      'dataAccident', 
      'locAccident', 
      'vehiculA.asigurat.nume', 
      'vehiculA.vehicul.marca', 
      'vehiculA.vehicul.nrInmatriculare', 
      'vehiculA.asigurare.denumire', 
      'vehiculA.asigurare.politaNr',
      'vehiculA.conducator.nume', 
      'vehiculB.asigurat.nume', 
      'vehiculB.vehicul.marca',
      'vehiculB.vehicul.nrInmatriculare', 
      'vehiculB.asigurare.denumire', 
      'vehiculB.asigurare.politaNr',
      'vehiculB.conducator.nume'
    ];
    
    for (const path of required) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], formData);
      if (!value?.trim()) {
        showToast(`Completati: ${path.split('.').pop()}`, true);
        return;
      }
    }

    // Validare email dacă e bifat
    if (wantEmail && !emailAddress.trim()) {
      showToast('Introduceti adresa de email', true);
      return;
    }
    if (wantEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      showToast('Adresa de email nu este valida', true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Eroare la generarea PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Download direct (funcționează pe toate browserele)
      const a = document.createElement('a');
      a.href = url;
      a.download = `Constatare_${formData.vehiculA.vehicul.nrInmatriculare}_${formData.vehiculB.vehicul.nrInmatriculare}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('PDF generat cu succes!');

      // Trimite pe email dacă e bifat
      if (wantEmail && emailAddress.trim()) {
        setEmailLoading(true);
        try {
          const emailResponse = await fetch('/api/send-pdf-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              formData, 
              email: emailAddress.trim() 
            })
          });
          if (emailResponse.ok) {
            showToast('PDF trimis si pe email!');
          } else {
            const errData = await emailResponse.json();
            showToast(errData.error || 'Eroare la trimiterea emailului', true);
          }
        } catch (emailErr) {
          showToast('Eroare la trimiterea emailului', true);
        } finally {
          setEmailLoading(false);
        }
      }
      
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setLoading(false);
    }
  }, [formData, showToast, wantEmail, emailAddress]);

  // Render formular vehicul
  const renderVehicleForm = (vehicle) => {
    const prefix = `vehicul${vehicle}`;
    const data = formData[prefix];
    const isA = vehicle === 'A';
    
    return (
      <div className={`form-section vehicle-${vehicle.toLowerCase()}`}>
        <div className="section-header">
          <div className={`section-icon ${isA ? 'blue' : 'orange'}`}>{isA ? '🚗' : '🚙'}</div>
          <h2>Vehicul {vehicle}</h2>
          <span className={`vehicle-badge badge-${vehicle.toLowerCase()}`}>{vehicle}</span>
        </div>

        <div className="subsection-title">6. Asigurat / Proprietar</div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nume <span className="required">*</span></label>
            <input type="text" className="form-input" value={data.asigurat.nume}
              onChange={e => updateFormData(`${prefix}.asigurat.nume`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Prenume</label>
            <input type="text" className="form-input" value={data.asigurat.prenume}
              onChange={e => updateFormData(`${prefix}.asigurat.prenume`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Adresa</label>
          <input type="text" className="form-input" value={data.asigurat.adresa}
            onChange={e => updateFormData(`${prefix}.asigurat.adresa`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Cod postal</label>
            <input type="text" className="form-input" value={data.asigurat.codPostal}
              onChange={e => updateFormData(`${prefix}.asigurat.codPostal`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara</label>
            <input type="text" className="form-input" value={data.asigurat.tara}
              onChange={e => updateFormData(`${prefix}.asigurat.tara`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Telefon / Email</label>
          <input type="text" className="form-input" value={data.asigurat.tel}
            onChange={e => updateFormData(`${prefix}.asigurat.tel`, e.target.value)} />
        </div>

        <div className="subsection-title">7. Vehicul cu motor</div>
        <div className="form-group">
          <label className="form-label">Marca, tip <span className="required">*</span></label>
          <input type="text" className="form-input" placeholder="ex: Volkswagen Golf 7" value={data.vehicul.marca}
            onChange={e => updateFormData(`${prefix}.vehicul.marca`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. inmatriculare <span className="required">*</span></label>
            <input type="text" className="form-input" placeholder="ex: B 123 ABC" value={data.vehicul.nrInmatriculare}
              onChange={e => updateFormData(`${prefix}.vehicul.nrInmatriculare`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara inmatricularii</label>
            <input type="text" className="form-input" value={data.vehicul.taraInmatriculare}
              onChange={e => updateFormData(`${prefix}.vehicul.taraInmatriculare`, e.target.value)} />
          </div>
        </div>

        <div className="subsection-title">7. Remorca (optional)</div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. inmatriculare remorca</label>
            <input type="text" className="form-input" placeholder="ex: B 456 XYZ" value={data.remorca?.nrInmatriculare || ''}
              onChange={e => updateFormData(`${prefix}.remorca.nrInmatriculare`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara inmatricularii</label>
            <input type="text" className="form-input" value={data.remorca?.taraInmatriculare || 'Romania'}
              onChange={e => updateFormData(`${prefix}.remorca.taraInmatriculare`, e.target.value)} />
          </div>
        </div>

        <div className="subsection-title">8. Societate de asigurare</div>
        <div className="form-group">
          <label className="form-label">Denumire <span className="required">*</span></label>
          <input type="text" className="form-input" placeholder="ex: Allianz, Grawe" value={data.asigurare.denumire}
            onChange={e => updateFormData(`${prefix}.asigurare.denumire`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. polita <span className="required">*</span></label>
            <input type="text" className="form-input" value={data.asigurare.politaNr}
              onChange={e => updateFormData(`${prefix}.asigurare.politaNr`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Carte Verde nr.</label>
            <input type="text" className="form-input" value={data.asigurare.carteVerdeNr}
              onChange={e => updateFormData(`${prefix}.asigurare.carteVerdeNr`, e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Valabil de la</label>
            <input type="date" className="form-input" value={data.asigurare.valabilDeLa}
              onChange={e => updateFormData(`${prefix}.asigurare.valabilDeLa`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Valabil pana la</label>
            <input type="date" className="form-input" value={data.asigurare.valabilPanaLa}
              onChange={e => updateFormData(`${prefix}.asigurare.valabilPanaLa`, e.target.value)} />
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Polita CASCO?</label>
          <RadioGroup value={data.casco || 'nu'} onChange={v => updateFormData(`${prefix}.casco`, v)} />
        </div>

        <div className="subsection-title">9. Conducator auto</div>
        <div className="form-group">
          <label className="checkbox-label" onClick={() => {
            if (data.conducatorEsteProprietar) uncheckOwnerToDriver(vehicle);
            else copyOwnerToDriver(vehicle);
          }}>
            <input type="checkbox" checked={data.conducatorEsteProprietar || false} onChange={() => {}} className="checkbox-input" />
            <span className="checkbox-text">Conducatorul este acelasi cu proprietarul</span>
          </label>
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nume <span className="required">*</span></label>
            <input type="text" className="form-input" value={data.conducator.nume}
              onChange={e => updateFormData(`${prefix}.conducator.nume`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Prenume</label>
            <input type="text" className="form-input" value={data.conducator.prenume}
              onChange={e => updateFormData(`${prefix}.conducator.prenume`, e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Data nasterii</label>
            <input type="date" className="form-input" value={data.conducator.dataNasterii}
              onChange={e => updateFormData(`${prefix}.conducator.dataNasterii`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara</label>
            <input type="text" className="form-input" value={data.conducator.tara}
              onChange={e => updateFormData(`${prefix}.conducator.tara`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Adresa</label>
          <input type="text" className="form-input" value={data.conducator.adresa}
            onChange={e => updateFormData(`${prefix}.conducator.adresa`, e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Telefon / Email</label>
          <input type="text" className="form-input" value={data.conducator.tel}
            onChange={e => updateFormData(`${prefix}.conducator.tel`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Permis nr.</label>
            <input type="text" className="form-input" value={data.conducator.permisNr}
              onChange={e => updateFormData(`${prefix}.conducator.permisNr`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Categoria</label>
            <input type="text" className="form-input" placeholder="A, B, C..." value={data.conducator.categorie}
              onChange={e => updateFormData(`${prefix}.conducator.categorie`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Permis valabil pana la</label>
          <input type="date" className="form-input" value={data.conducator.permisValabil}
            onChange={e => updateFormData(`${prefix}.conducator.permisValabil`, e.target.value)} />
        </div>

        <div className="subsection-title">11. Pagube vizibile</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <textarea className="form-textarea" placeholder="ex: Bara fata sparta, far stang spart" rows="2"
            value={data.pagube} onChange={e => updateFormData(`${prefix}.pagube`, e.target.value)} />
        </div>
      </div>
    );
  };

  const currentStepData = WIZARD_STEPS.find(s => s.id === currentStep);

  // Render step content - 5 PASI
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Date accident
        return (
          <div className="form-section full-width">
            <div className="section-header">
              <div className="section-icon blue">📍</div>
              <h2>Date despre accident</h2>
            </div>
            
            <div className="form-row">
              <div className="form-group no-margin">
                <label className="form-label">1. Data accidentului <span className="required">*</span></label>
                <input type="date" className="form-input" value={formData.dataAccident}
                  onChange={e => updateFormData('dataAccident', e.target.value)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Ora</label>
                <input type="time" className="form-input" value={formData.oraAccident}
                  onChange={e => updateFormData('oraAccident', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group no-margin">
                <label className="form-label">2. Locul accidentului <span className="required">*</span></label>
                <input type="text" className="form-input" placeholder="ex: Str. Mihai Eminescu nr. 10, Bucuresti"
                  value={formData.locAccident} onChange={e => updateFormData('locAccident', e.target.value)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Tara</label>
                <input type="text" className="form-input" value={formData.taraAccident}
                  onChange={e => updateFormData('taraAccident', e.target.value)} />
              </div>
            </div>

            <div className="form-row-3">
              <div className="form-group no-margin">
                <label className="form-label">3. Vatamari corporale?</label>
                <RadioGroup value={formData.vatamariCorporale} onChange={v => updateFormData('vatamariCorporale', v)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">4. Pagube alte vehicule?</label>
                <RadioGroup value={formData.pagubeVehicule} onChange={v => updateFormData('pagubeVehicule', v)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Pagube alte obiecte?</label>
                <RadioGroup value={formData.pagubeObiecte} onChange={v => updateFormData('pagubeObiecte', v)} />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 8, marginBottom: 0 }}>
              <label className="form-label">5. Martori</label>
              <textarea className="form-textarea" placeholder="ex: Ion Popescu, Str. Victoriei 5, 0722.123.456"
                rows="2" value={formData.martori} onChange={e => updateFormData('martori', e.target.value)} />
            </div>
          </div>
        );

      case 2: // VEHICULE A & B - UNITE
        return (
          <div className="form-grid">
            {renderVehicleForm('A')}
            {renderVehicleForm('B')}
          </div>
        );

      case 3: // Imprejurari
        return (
          <div className="form-section full-width">
            <div className="section-header">
              <div className="section-icon green">📋</div>
              <h2>12. Imprejurari</h2>
            </div>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>Bifati situatiile pentru fiecare vehicul:</p>
            <div className="imprejurari-grid">
              <div className="imprejurari-column col-a">
                <div className="imprejurari-header header-a">VEHICUL A ({formData.imprejurariA.length})</div>
                <div className="imprejurari-list">
                  {IMPREJURARI_DATA.map(item => (
                    <div key={item.id} className={`imprejurare-item ${formData.imprejurariA.includes(item.id) ? 'selected-a' : ''}`}
                      onClick={() => toggleImprejurare('A', item.id)}>
                      <div className="checkbox-box">{formData.imprejurariA.includes(item.id) && '✔'}</div>
                      <span><strong>{item.id}.</strong> {item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="imprejurari-column col-b">
                <div className="imprejurari-header header-b">VEHICUL B ({formData.imprejurariB.length})</div>
                <div className="imprejurari-list">
                  {IMPREJURARI_DATA.map(item => (
                    <div key={item.id} className={`imprejurare-item ${formData.imprejurariB.includes(item.id) ? 'selected-b' : ''}`}
                      onClick={() => toggleImprejurare('B', item.id)}>
                      <div className="checkbox-box">{formData.imprejurariB.includes(item.id) && '✔'}</div>
                      <span><strong>{item.id}.</strong> {item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Schita
        return (
          <div className="form-section full-width sketch-section">
            <div className="section-header">
              <div className="section-icon blue">🎨</div>
              <h2>13. Schita accidentului</h2>
            </div>
            <div className="sketch-mode-toggle">
              <button type="button" className={`mode-btn ${sketchMode === 'manual' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('manual')}>Desen Manual</button>
              <button type="button" className={`mode-btn ${sketchMode === 'ai' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('ai')}>Generare AI</button>
              <button type="button" className={`mode-btn ${sketchMode === 'select' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('select')}>Alege Scenariu</button>
            </div>

            {sketchMode === 'manual' && (
              <div className="sketch-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 0, textAlign: 'center' }}>
                  In PDF va aparea o grila pentru desen manual.
                </p>
              </div>
            )}

            {sketchMode === 'ai' && (
              <div className="sketch-ai-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Descrieti accidentul si AI-ul va genera o schita. <strong>Vehicul A = al dumneavoastra.</strong>
                </p>
                <div className="form-group">
                  <label className="form-label">Alege un exemplu sau descrie accidentul:</label>
                  <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, marginBottom: 8, lineHeight: 1.5 }}>
                    💡 Daca schita generata nu este corecta, incercati sa adaugati mai multe detalii in descriere.
                  </p>
                  <select className="form-select" 
                    onChange={e => { if(e.target.value) setDescriereAccident(e.target.value); }}
                    value="">
                    <option value="">-- Selectati un exemplu --</option>
                    {EXEMPLE_DESCRIERI.map((ex, i) => (
                      <option key={i} value={ex}>{ex.substring(0, 60)}...</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Descrieti accidentul</label>
                  <textarea className="form-textarea" placeholder="Ex: Mergeam pe strada principala..." rows="3"
                    value={descriereAccident} onChange={e => setDescriereAccident(e.target.value)} />
                </div>
                <button type="button" className="btn-generate-sketch" onClick={genereazaSchita}
                  disabled={sketchLoading || !descriereAccident.trim()}>
                  {sketchLoading ? <><span className="spinner"></span> Se genereaza...</> : 'Genereaza Schita'}
                </button>
                {sketchError && <p style={{ color: '#DC2626', marginTop: 10 }}>{sketchError}</p>}
              </div>
            )}

            {sketchMode === 'select' && (
              <div className="sketch-ai-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Alegeti un scenariu predefinit care se potriveste cu accidentul dumneavoastra:
                </p>
                <div className="form-group">
                  <label className="form-label">Scenariu:</label>
                  <select className="form-select" 
                    onChange={e => selectScenario(e.target.value)}
                    value={sketchData?.scenarioId || ''}>
                    <option value="">-- Selectati un scenariu --</option>
                    {SCENARIO_LIST.map(s => (
                      <option key={s.id} value={s.id}>{s.name} - {s.description}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="sketch-display" style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'center', overflow: 'hidden', maxWidth: '100%' }}>
                <div style={{ transform: 'scale(0.85)', transformOrigin: 'center center' }}>
                  {sketchData ? (
                    <AccidentSketch data={sketchData} width={360} height={260} />
                  ) : (
                    <ManualSketchGrid width={360} height={260} />
                  )}
                </div>
              </div>
              <p style={{ fontSize: 13, color: '#6B7280', marginTop: 12, textAlign: 'center' }}>
                {sketchData ? 'Schita generata' : 'Schita se completeaza manual sau cu AI.'}
              </p>
              {sketchData && (
                <div style={{ marginTop: 12, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {sketchMode === 'ai' && (
                    <button type="button" onClick={genereazaSchita} className="btn-regenerate">Regenereaza</button>
                  )}
                  <button type="button" onClick={() => { 
                    setSketchData(null); 
                    setFormData(prev => ({ ...prev, sketchData: null })); 
                  }} className="btn-clear">Sterge</button>
                </div>
              )}
            </div>
          </div>
        );

      case 5: // Finalizare
        return (
          <>
            <div className="form-section full-width">
              <div className="section-header">
                <div className="section-icon gray">📝</div>
                <h2>14. Observatii</h2>
              </div>
              <div className="observatii-grid">
                <div className="observatii-column col-a">
                  <div className="observatii-header header-a">VEHICUL A</div>
                  <textarea className="form-textarea" rows="3" placeholder="Observatii pentru vehiculul A..."
                    value={formData.observatiiA} onChange={e => updateFormData('observatiiA', e.target.value)} />
                </div>
                <div className="observatii-column col-b">
                  <div className="observatii-header header-b">VEHICUL B</div>
                  <textarea className="form-textarea" rows="3" placeholder="Observatii pentru vehiculul B..."
                    value={formData.observatiiB} onChange={e => updateFormData('observatiiB', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="generate-section green">
              <h2>Genereaza Constatarea Amiabila</h2>
              <p>Verifica datele, apoi descarca PDF-ul oficial completat</p>
              {sketchData && <p style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>Schita va fi inclusa in PDF</p>}
              
              {/* Opțiune email */}
              <div className="email-option">
                <label className="email-checkbox-label" onClick={() => setWantEmail(!wantEmail)}>
                  <input 
                    type="checkbox" 
                    checked={wantEmail} 
                    onChange={() => {}} 
                    className="email-checkbox"
                  />
                  <span>Vreau sa primesc PDF-ul si pe email</span>
                </label>
                
                {wantEmail && (
                  <div className="email-input-container">
                    <input 
                      type="email" 
                      className="email-input" 
                      placeholder="adresa@email.com"
                      value={emailAddress}
                      onChange={e => setEmailAddress(e.target.value)}
                    />
                    <p className="email-hint">📧 Vei primi PDF-ul si pe email, util pentru telefon!</p>
                  </div>
                )}
              </div>

              <button className="btn-generate" onClick={generatePDF} disabled={loading || emailLoading}>
                {loading ? <><span className="spinner"></span> Se genereaza...</> : 
                 emailLoading ? <><span className="spinner"></span> Se trimite email...</> :
                 'Descarca Amiabila (PDF)'}
              </button>
            </div>

            <div className="reset-section">
              <p>Ai completat si descarcat formularul? Poti sterge definitiv toate datele introduse.</p>
              <button type="button" className="btn-reset" onClick={resetForm}>Sterge toate datele</button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <main className="form-main" ref={formContainerRef}>
        <div className="form-toolbar">
          <div className="autosave-indicator">
            <span className="autosave-dot"></span>
            Datele se salveaza automat in browser
          </div>
        </div>

        <WizardStepper 
          currentStep={currentStep} 
          onStepClick={goToStep}
          completedSteps={completedSteps}
        />

        <StepIntro step={currentStepData} />

        {renderStepContent()}

        <div className="wizard-navigation">
          {currentStep > 1 && (
            <button type="button" className="btn-wizard btn-prev" onClick={prevStep}>
              ← Inapoi
            </button>
          )}
          {currentStep < WIZARD_STEPS.length && (
            <button type="button" className="btn-wizard btn-next" onClick={nextStep}>
              Continua →
            </button>
          )}
        </div>
      </main>

      <div className={`toast ${toast.show ? 'show' : ''} ${toast.error ? 'error' : ''}`}>{toast.message}</div>
    </>
  );
}

export default FormularPage;